/**
 * Videos and courses for the signed-in coach — coach auth, her account, and
 * delegation to `~/lib/server/learning` — plus the one video a public
 * placement shows every visitor. Access is decided on the server from
 * the same account value every coach screen reads, and from her NOVA assets,
 * whose approval completes the course weeks that build them.
 *
 * Aliases: coach videos api, coach courses api, placement videos, mark week complete
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { courseSlugSchema } from "~/lib/shared/learning/admin-inputs";
import {
  isPublicVideoPlacementKey,
  type PublicVideoPlacementKey,
} from "~/lib/shared/learning/placements";

// Type-only re-exports, erased at build, so a route component never imports
// from `~/lib/server`.
export type {
  CoachCourse,
  CoachCourseLink,
  CoachCourseMaterial,
  CoachCourseWeek,
  CoachCourseWeekCard,
  CompleteCourseWeekResult,
} from "~/lib/server/learning/coach-courses";

/** The video each placement shows this coach; absent where none is set. */
export const getMyPlacementVideos = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { buildCoachAccount } = await import("~/lib/server/coach-account");
    const { resolvePlacementVideos } =
      await import("~/lib/server/learning/placements");
    return resolvePlacementVideos(await buildCoachAccount(context.coach));
  });

/**
 * The help videos for whoever is signed in, a client or a coach: the role is
 * decided on the server and only that role's topics come back.
 */
export const getMyHelpVideos = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { resolveMyHelpVideos } =
      await import("~/lib/server/learning/placements");
    return resolveMyHelpVideos(context.user);
  });

const publicPlacementInput = z.object({
  placement: z.custom<PublicVideoPlacementKey>(
    (value) => typeof value === "string" && isPublicVideoPlacementKey(value),
    { message: "Unknown public placement" },
  ),
});

/**
 * No auth: a public placement is on a page visitors see signed out, and the
 * validator refuses every placement that is not one.
 */
export const getPublicPlacementVideo = createServerFn({ method: "GET" })
  .validator(publicPlacementInput)
  .handler(async ({ data }) => {
    const { resolvePublicPlacementVideo } =
      await import("~/lib/server/learning/placements");
    return resolvePublicPlacementVideo(data.placement);
  });

export const getMyCourses = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async () => {
    const { listPublishedCourses } =
      await import("~/lib/server/learning/coach-courses");
    return listPublishedCourses();
  });

const courseInput = z.object({ slug: courseSlugSchema });
const weekInput = courseInput.extend({ number: z.number().int().positive() });
// Completion is keyed by the week's id, not its number: the number can change
// under the coach between page load and the request (a reorder, a delete).
const completeWeekInput = courseInput.extend({ weekId: z.string().uuid() });

export const getMyCourse = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(courseInput)
  .handler(async ({ data, context }) => {
    const { buildCoachAccount } = await import("~/lib/server/coach-account");
    const { getCoachAssetsWithApprovals } =
      await import("~/lib/server/nova/assets/coach-assets");
    const { getCoachCourse } =
      await import("~/lib/server/learning/coach-courses");
    const [account, { approved }] = await Promise.all([
      buildCoachAccount(context.coach),
      getCoachAssetsWithApprovals(context.coach),
    ]);
    return getCoachCourse({
      coachId: context.coach.id,
      lifecycle: account.lifecycle,
      approvedAssets: approved,
      slug: data.slug,
    });
  });

export const getMyCourseWeek = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(weekInput)
  .handler(async ({ data, context }) => {
    const { buildCoachAccount } = await import("~/lib/server/coach-account");
    const { getCoachAssetsWithApprovals } =
      await import("~/lib/server/nova/assets/coach-assets");
    const { getCoachCourseWeek } =
      await import("~/lib/server/learning/coach-courses");
    const [account, { assets, approved }] = await Promise.all([
      buildCoachAccount(context.coach),
      getCoachAssetsWithApprovals(context.coach),
    ]);
    return getCoachCourseWeek({
      coachId: context.coach.id,
      lifecycle: account.lifecycle,
      approvedAssets: approved,
      assets,
      slug: data.slug,
      number: data.number,
    });
  });

export const completeMyCourseWeek = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(completeWeekInput)
  .handler(async ({ data, context }) => {
    const { buildCoachAccount } = await import("~/lib/server/coach-account");
    const { getCoachAssetsWithApprovals } =
      await import("~/lib/server/nova/assets/coach-assets");
    const { completeCourseWeek } =
      await import("~/lib/server/learning/coach-courses");
    const [account, { approved }] = await Promise.all([
      buildCoachAccount(context.coach),
      getCoachAssetsWithApprovals(context.coach),
    ]);
    return completeCourseWeek({
      coachId: context.coach.id,
      lifecycle: account.lifecycle,
      approvedAssets: approved,
      slug: data.slug,
      weekId: data.weekId,
    });
  });
